<script lang="ts" setup>
import {
  type PropType,
  ref,
  nextTick,
  onUnmounted,
  reactive,
  onMounted,
  watch,
  computed,
  CSSProperties,
  inject,
  provide,
} from 'vue';
import { PopoverColorEnum, PopoverPositionEnum } from './enums';
import { debounce } from './utils';
import type { PopoverPositionType } from './types';
import { useRootFontSize } from 'tir-pm-toolkit';

/**
 * * Свойства
 */
const props = defineProps({
  /**
   * * Позиция
   */
  position: {
    type: String as PropType<PopoverPositionEnum>,
    default: PopoverPositionEnum.Auto,
  },
  /**
   * * Кастомна позиция
   */
  customPosition: {
    type: Object as PropType<PopoverPositionType>,
  },
  /**
   * * Цвет
   */
  color: {
    type: String as PropType<PopoverColorEnum>,
    default: PopoverColorEnum.White,
  },
  /**
   * * Ширина
   */
  width: { type: String, default: 'auto' },
  /**
   * * Классы контента
   */
  classes: { type: String, default: '' },
  /**
   * * Режим тултипа
   */
  isTooltip: { type: Boolean, default: false },
  /**
   * * Неактивность
   */
  isDisabled: { type: Boolean, default: false },
  /**
   * * Неактивность телепорта
   */
  isNoTeleport: { type: Boolean, default: false },
  /**
   * * Скрыть треугольник поповера
   */
  isHideTriangle: { type: Boolean, default: true },
  /**Отключение потери фокуса при клике по контенту */
  disableContentBlur: { type: Boolean, default: false },
  /**
   * * Добавить задержку при закрытии тултипа
   */
  tooltipCloseDelay: { type: Number, default: 50 },
  /**
   * * Добавить задержку при открытии тултипа
   */
  tooltipOpenDelay: { type: Number, default: 150 },
  /**Дополнительный отступ для уголка по X оси */
  additionalTriangleXPosition: { type: Number, default: 0 },
  /**
   * * Паддинг
   */
  padding: { type: String, default: '0' },
});

const { rootFontSizePx } = useRootFontSize();

/**
 * * События
 */
const emit = defineEmits<{
  /**
   * * Открытие
   */
  (e: 'open', target: HTMLElement): void;
  /**
   * * Закрытие
   */
  (e: 'close', target: HTMLElement): void;
  /**
   * * Фокус
   */
  (e: 'focus', target: HTMLElement): void;
  /**
   * * Снятие фокуса
   */
  (e: 'blur', target: HTMLElement): void;
}>();
/**
 * * Видимость
 */
const visible = ref(false);
/**
 * * Элемент
 */
const $element = ref<HTMLElement>();
/**
 * * Поповер
 */
const $content = ref<HTMLElement>();
/**
 * * Внутри
 */
const $inner = ref<HTMLElement>();
/**
 * * Треугольник
 */
const $triangle = ref<HTMLElement>();
/**
 * * Координаты поповера
 */
const content = reactive({ x: 0, y: 0 });
/**
 * * Стили кастомной позиции
 */
const customPositionStyle = ref<CSSProperties>();
/**
 * * Координаты треугольника
 */
const triangle = reactive({ x: 0, y: 0 });
/**
 * * Позиция
 */
const _position = ref(props.position);
const isCloseAfterDelay = ref<boolean>(true);
const elementRect = ref<DOMRect>();
const isOpenAfterDelay = ref<boolean>(true);

/**
 * * Клик по элементу
 */
function focus(event: MouseEvent) {
  if (visible.value && !props.isTooltip) close(event?.target as any);
  else {
    if (visible.value || props.isDisabled) return;
    if (props.isTooltip) {
      $element.value?.addEventListener('mouseout', mouseout);
      debounceOpen();
      isOpenAfterDelay.value = true;
    } else open();
    if (!$element.value) return;
    emit('focus', $element.value);
  }
}

/**
 * * Высчитать позицию
 */
function calc(
  p: PopoverPositionEnum,
  about: DOMRect,
  rect: DOMRect,
  i = 0,
): { x: number; y: number; position: PopoverPositionEnum } {
  const bw = getComputedStyle(document.documentElement)
    .getPropertyValue('--tir-popover-tr')
    .replace('rem', '');
  const br = getComputedStyle(document.documentElement)
    .getPropertyValue('--tir-popover-br')
    .replace('rem', '');
  const min = Number(bw) * 2 + Number(br);
  const settings = { x: 0, y: 0, position: p };
  const ignore = i == 4;
  if (ignore) {
    const indets = [
      about.left,
      document.documentElement.clientWidth - about.right,
      about.top,
      document.documentElement.clientHeight - about.bottom,
    ];
    switch (indets.indexOf(Math.max(...indets))) {
      case 0:
        p = PopoverPositionEnum.Left;
        break;
      case 1:
        p = PopoverPositionEnum.Right;
        break;
      case 2:
        p = PopoverPositionEnum.Top;
        break;
      case 3:
        p = PopoverPositionEnum.Bottom;
        break;
    }
    settings.position = p;
  }

  switch (p) {
    case PopoverPositionEnum.Top:
      settings.x = about.left + about.width / 2 - rect.width / 2;
      settings.y = about.top - rect.height - 8;

      if (settings.x < 0) settings.x = 0;
      else if (settings.x + rect.width > document.documentElement.clientWidth)
        settings.x = document.documentElement.clientWidth - rect.width;

      if (
        !ignore &&
        (settings.y < 0 ||
          about.right - document.documentElement.clientWidth >= about.width / 2 - min ||
          about.left <= -about.width / 2 + min)
      )
        return calc(PopoverPositionEnum.Right, about, rect, i + 1);
      break;
    case PopoverPositionEnum.Left:
      settings.y = about.top + about.height / 2 - rect.height / 2;
      settings.x = about.left - rect.width - 8;

      if (settings.y < 0) settings.y = 0;
      else if (settings.y + rect.height > document.documentElement.clientHeight)
        settings.y = document.documentElement.clientHeight - rect.height;

      if (
        !ignore &&
        (settings.x < 0 ||
          about.bottom - document.documentElement.clientHeight >= about.height / 2 - min ||
          about.top <= -about.height / 2 + min)
      )
        return calc(PopoverPositionEnum.Top, about, rect, i + 1);
      break;
    case PopoverPositionEnum.Right:
      settings.y = about.top + about.height / 2 - rect.height / 2;
      settings.x = about.right + 8;

      if (settings.y < 0) settings.y = 0;
      else if (settings.y + rect.height > document.documentElement.clientHeight)
        settings.y = document.documentElement.clientHeight - rect.height;

      if (
        !ignore &&
        (settings.x + rect.width > document.documentElement.clientWidth ||
          about.bottom - document.documentElement.clientHeight >= about.height / 2 - min ||
          about.top <= -about.height / 2 + min)
      )
        return calc(PopoverPositionEnum.Bottom, about, rect, i + 1);
      break;
    case PopoverPositionEnum.Bottom:
      settings.x = about.left + about.width / 2 - rect.width / 2;
      settings.y = about.bottom + 8;

      if (settings.x < 0) settings.x = 0;
      else if (settings.x + rect.width > document.documentElement.clientWidth)
        settings.x = document.documentElement.clientWidth - rect.width;

      if (
        !ignore &&
        (settings.y + rect.height > document.documentElement.clientHeight ||
          about.right - document.documentElement.clientWidth >= about.width / 2 - min ||
          about.left <= -about.width / 2 + min)
      )
        return calc(PopoverPositionEnum.Left, about, rect, i + 1);
      break;
    case PopoverPositionEnum.Auto:
      return calc(PopoverPositionEnum.Top, about, rect);
  }
  return settings;
}
// const htmlElement = document.documentElement;

const getPositionReplaced = (elRect: DOMRect, positionStyleStr: string) => {
  return positionStyleStr
    .replaceAll('$el:width', `${elRect.width / rootFontSizePx.value}rem`)
    .replaceAll('$el:height', `${elRect.height / rootFontSizePx.value}rem`);
};

/**
 * * Инициализация позиций элементов
 */
async function init(skipScale?: boolean) {
  await nextTick();
  if (!$element.value || !$content.value) return;
  await nextTick();
  elementRect.value = $element.value.getBoundingClientRect();

  await nextTick();
  const contentRect = $content.value.getBoundingClientRect();

  if (!skipScale) {
    contentRect.width = scaleByPercent(contentRect.width);
    contentRect.height = scaleByPercent(contentRect.height);
  }

  if (props.customPosition) {
    let left: string | undefined;
    let top: string | undefined;
    let width: string | undefined;

    if (props.customPosition.left) {
      left = `calc(${elementRect.value.left / rootFontSizePx.value}rem + ${getPositionReplaced(
        elementRect.value,
        props.customPosition.left,
      )})`;
    } else if (props.customPosition.right) {
      left = `calc(${
        elementRect.value.right / rootFontSizePx.value - contentRect.width / rootFontSizePx.value
      }rem + ${getPositionReplaced(elementRect.value, props.customPosition.right)})`;
    }
    if (props.customPosition.top) {
      top = `calc(${elementRect.value.top / rootFontSizePx.value}rem + ${getPositionReplaced(
        elementRect.value,
        props.customPosition.top,
      )})`;
    } else if (props.customPosition.bottom) {
      top = `calc(${
        elementRect.value.top / rootFontSizePx.value - contentRect.height / rootFontSizePx.value
      }rem - ${getPositionReplaced(elementRect.value, props.customPosition.bottom)})`;
    }

    if (props.customPosition.left && props.customPosition.right) {
      width = `calc(${elementRect.value.width / rootFontSizePx.value}rem - ${getPositionReplaced(
        elementRect.value,
        props.customPosition.left,
      )} - ${getPositionReplaced(elementRect.value, props.customPosition.right)})`;
    }

    customPositionStyle.value = {
      left,
      top,
      maxWidth: width,
      minWidth: width,
    };
    return;
  }

  customPositionStyle.value = undefined;

  const contentSettings = calc(props.position, elementRect.value, contentRect);

  const remFactor = rootFontSizePx.value / rootFontSizePx.value;

  content.x = contentSettings.x / remFactor;
  content.y = contentSettings.y / remFactor;
  _position.value = contentSettings.position;

  await nextTick();

  if (!$triangle.value) return;
  const triangleRect = $triangle.value.getBoundingClientRect();

  if (!skipScale) {
    triangleRect.width = scaleByPercent(triangleRect.width);
    triangleRect.height = scaleByPercent(triangleRect.height);
  }

  const triangleSettings = calc(_position.value, elementRect.value, triangleRect);
  triangle.x = triangleSettings.x + props.additionalTriangleXPosition;
  triangle.y = triangleSettings.y;

  switch (_position.value) {
    case PopoverPositionEnum.Left:
      triangle.x -= 1;
      break;
    case PopoverPositionEnum.Right:
      triangle.x += 1;
      break;
    case PopoverPositionEnum.Top:
      triangle.y -= 1;
      break;
    case PopoverPositionEnum.Bottom:
      triangle.y += 1;
      break;
  }
}
/**Поднять значение, без учета scale анимации */
const scaleByPercent = (_value: number) => (_value / 80) * 100;

const elementResizeObserver = new ResizeObserver(initOnChange);

/**Селектор скролла (для обновления позиции) */
const scrollSelector = inject<string>('popover-scroll-selector', '.tir-scroll .hide-scroll');
/**Контейнер скролла */
const scrollContainer = ref<HTMLElement>();
provide('parentScrollContainer', scrollContainer);
/**
 * * При загрузке компонента
 */
async function mountComponent() {
  scrollContainer.value =
    ($element.value?.closest(scrollSelector) as HTMLElement | undefined) ??
    inject('parentScrollContainer')?.value;

  if (!$element.value || props.isDisabled) return;
  elementRect.value = $element.value.getBoundingClientRect();
  $element.value.addEventListener('click', focus);
  elementResizeObserver.observe($element.value);

  if (computedWidth.value) {
    const elementRect = $element.value?.getBoundingClientRect();
    const contentSettings = calc(props.position, elementRect, {
      width: computedWidth.value,
    } as any);
    content.x = contentSettings.x;
    content.y = contentSettings.y;
  }

  if (!props.isTooltip) return;
  $element.value.addEventListener('mouseover', focus);
}
/**Значение стиля transform-origin для анимации */
const transfromOriginByTriangle = computed(
  () =>
    `${triangle.x / rootFontSizePx.value - content.x / rootFontSizePx.value}rem ${
      triangle.y / rootFontSizePx.value - content.y / rootFontSizePx.value
    }rem`,
);

watch(() => props.isDisabled, mountComponent);

onMounted(mountComponent);

const cancelClose = (e: MouseEvent) => {
  const target = e.target as HTMLElement;
  if (!target) return;
  blur(target);
};

/**
 * * Открыть
 */
async function open() {
  if (visible.value || !isOpenAfterDelay.value) return;
  visible.value = true;
  await init();

  if (!props.isDisabled) {
    window.addEventListener('click', click);
    if (props.isTooltip && $element.value && $inner.value && $content.value) {
      $element.value.addEventListener('mouseover', cancelClose);
      $content.value.addEventListener('mouseover', cancelClose);
      $inner.value.addEventListener('mouseout', mouseout);
      window.addEventListener('scroll', windowOnScroll);
      window.addEventListener('resize', windowOnResize);
      scrollContainer.value?.addEventListener('scroll', windowOnScroll);
    } else {
      window.addEventListener('scroll', initOnChange);
      window.addEventListener('resize', initOnChange);
      scrollContainer.value?.addEventListener('scroll', initOnChange);
    }
  }
  if ($inner.value) emit('open', $inner.value);
}

const debounceOpen = debounce(open, props.tooltipOpenDelay);

/**
 * * Закрыть
 */
function close(target?: HTMLElement) {
  if (props.isTooltip && !isCloseAfterDelay.value) return;

  const isContentClicked =
    props.disableContentBlur &&
    target &&
    $content.value &&
    (target == $content.value || $content.value?.contains(target));

  if (!visible.value || isContentClicked) return;
  visible.value = false;
  if (!props.isDisabled) {
    window.removeEventListener('click', click);
    if (props.isTooltip && $element.value && $inner.value && $content.value) {
      $element.value.removeEventListener('mouseout', mouseout);
      $inner.value.removeEventListener('mouseout', mouseout);
      window.removeEventListener('scroll', windowOnScroll);
      window.removeEventListener('resize', windowOnResize);
      $content.value.removeEventListener('mouseover', cancelClose);
      $element.value.removeEventListener('mouseover', cancelClose);
      scrollContainer.value?.removeEventListener('scroll', windowOnScroll);
    } else {
      window.removeEventListener('scroll', initOnChange);
      window.removeEventListener('resize', initOnChange);
      scrollContainer.value?.removeEventListener('scroll', initOnChange);
    }
  }
  if (!$inner.value) return;

  emit('close', $inner.value);
}
/**Функция для вызова init после скролла или ресайза */
function initOnChange() {
  init(true);
}
const debounceClose = debounce(close, props.tooltipCloseDelay);

/**Закрытие при прокрутке страницы */
function windowOnScroll() {
  isCloseAfterDelay.value = true;
  close();
}
/**Закрытие при изменении размера окна */
function windowOnResize() {
  close();
}
/**
 * * Убрать фокус
 */
function blur(target: HTMLElement) {
  if (
    !target ||
    !$inner.value ||
    $inner.value == target ||
    $inner.value.contains(target) ||
    !$element.value ||
    $element.value == target ||
    $element.value.contains(target) ||
    !$triangle.value ||
    $triangle.value == target ||
    $triangle.value.contains(target)
  ) {
    if (props.isTooltip) {
      isCloseAfterDelay.value = false;
      isOpenAfterDelay.value = false;
    }
    return;
  }
  emit('blur', target);
  if (props.isTooltip) {
    debounceClose(target);
    isCloseAfterDelay.value = true;
    isOpenAfterDelay.value = false;
  } else close(target);
}
/**
 * * Блюр щелчком
 */
function click(e: Event) {
  const target = e.target as HTMLElement;
  blur(target);
}
/**
 * * Блюр наведением
 */
function mouseout(e: MouseEvent) {
  const target = e.relatedTarget as HTMLElement;
  blur(target);
}
/**
 * * При разрушении компонента
 */
onUnmounted(() => {
  elementResizeObserver.disconnect();
  close();
});

/**
 * * Вычисляемая ширина
 */
const computedWidth = computed(() => {
  if (!props.width) return;

  let localWidth = 0;

  if (props.width.includes('%-el') && elementRect.value) {
    const index = props.width.indexOf('%-el');
    localWidth = Number(props.width.slice(0, index)) || 0;
    localWidth = (elementRect.value.width / 100) * localWidth;
  }

  return localWidth ? `${localWidth / rootFontSizePx.value}rem` : props.width;
});

/**
 * * Поделиться с родителем
 */
defineExpose({
  open,
  close,
  init,
  visible,
});
</script>

<template>
  <div class="tir-popover_element" ref="$element">
    <teleport to="body" :disabled="isNoTeleport">
      <transition name="tir-popover_animation">
        <div v-if="visible" class="tir-popover_container" :class="color">
          <div
            ref="$content"
            class="tir-popover_content"
            :style="
              customPositionStyle
                ? customPositionStyle
                : {
                    left: `${content.x / rootFontSizePx}rem`,
                    top: `${content.y / rootFontSizePx}rem`,
                    'transform-origin': transfromOriginByTriangle,
                  }
            "
            :class="color"
          >
            <div
              ref="$inner"
              class="inner"
              :class="[_position, color, classes]"
              :style="{ width: computedWidth, padding }"
            >
              <slot name="content" />
            </div>
          </div>
          <div
            ref="$triangle"
            class="tir-popover_triangle"
            :class="[_position, color, { 'is-hide': isHideTriangle }]"
            :style="{
              left: `${triangle.x / rootFontSizePx}rem`,
              top: `${triangle.y / rootFontSizePx}rem`,
            }"
          />
        </div>
      </transition>
    </teleport>
    <slot :isOpened="visible" :toClose="close" :toOpen="open" />
  </div>
</template>
<style lang="scss" scoped>
$color: var(--tir-popover-c);
$shadow: var(--tir-popover-sh);
$triangle: var(--tir-popover-tr);
$radius: var(--tir-popover-br);
$min: calc($triangle * 2 + $radius * 2);
$transition: var(--tir-popover-ts);

.tir-popover {
  &_element {
    display: inline-flex;
    width: auto;
    height: fit-content;
  }

  &_content {
    position: fixed;
    padding: $triangle;
    color: $color;

    .inner {
      background-color: var(--tir-popover-bg);
      box-shadow: $shadow;
      border-radius: $radius;
      min-height: $min;
      min-width: $min;
      padding: var(--tir-popover-py) var(--tir-popover-px);

      &.top,
      &.bottom {
        max-width: 100%;
      }

      &.left,
      &.right {
        max-height: 100%;
      }
    }

    &.top,
    &.bottom {
      max-width: 100%;
    }

    &.left,
    &.right {
      max-height: 100%;
    }
  }

  &_triangle {
    position: fixed;
    max-height: 0;
    max-width: 0;
    border-style: solid;
    border-color: transparent;
    z-index: 10000;
    border-width: var(--tir-popover-tr);

    &.is-hide {
      opacity: 0;
      display: none;
    }

    &.top {
      border-top-color: var(--tir-popover-bg);
      border-bottom-width: 0;
    }

    &.bottom {
      border-top-width: 0;
      border-bottom-color: var(--tir-popover-bg);
    }

    &.left {
      border-right-width: 0;
      border-left-color: var(--tir-popover-bg);
    }

    &.right {
      border-left-width: 0;
      border-right-color: var(--tir-popover-bg);
    }
  }

  &_container {
    position: absolute;
    z-index: 10000;

    --tir-popover-tr: 0.625rem;
    --tir-popover-ts: var(--tir-popover-transition_duration)
      var(--tir-popover-transition_timing_function);
    --tir-popover-br: var(--tir-popover-content-br);

    &:not(.white) {
      --tir-popover-c: var(--text-primary-inverted, #3d424b);
    }

    &.white {
      --tir-popover-bg: var(--tir-popover-white);
      --tir-popover-sh: var(--tir-popover-white-shadow);
    }

    &.black {
      --tir-popover-bg: var(--bg-base-inverted, #272d37);
      color: var(--bg-base, #fff);
    }
  }

  &_animation-enter-active,
  &_animation-leave-active {
    transition: $transition;

    > .tir-popover_triangle,
    > .tir-popover_content {
      transition: $transition;
      transition-property: opacity, transform;
      opacity: 1;
      transform: scale(1);
    }
  }

  &_animation-enter-from,
  &_animation-leave-to {
    transition: $transition;

    > .tir-popover_triangle,
    > .tir-popover_content {
      transition: $transition;
      transition-property: opacity, transform;
      opacity: 0;
      transform: scale(0.8);
    }
  }
}
</style>
